import { presentLexError } from '@/lib/user-notice';
import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getLawById, getArticlesByLawId } from '@/lib/laws';
import type { Law, Article } from '@/types';

function cleanDescription(description: string | null): string {
  if (!description) return '';
  const match = description.match(/^%[A-Za-z0-9_.-]+\s*/);
  return match ? description.slice(match[0].length).trim() : description.trim();
}

function formatLawDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

const INDEX_COLORS = ['#0f5bd8', '#0c8f7a', '#7a52f4', '#e07a1f', '#d24b5b'];

export function LawDetailPage() {
  const { lawId } = useParams<{ lawId: string }>();
  const navigate = useNavigate();
  const [law, setLaw] = useState<Law | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showArticleIndex, setShowArticleIndex] = useState(false);
  const [indexSearch, setIndexSearch] = useState('');

  useEffect(() => {
    if (!lawId) return;
    setError('');
    Promise.all([getLawById(lawId), getArticlesByLawId(lawId)])
      .then(([lawData, articlesData]) => {
        setLaw(lawData);
        setArticles(articlesData);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error al cargar la ley.'))
      .finally(() => setLoading(false));
  }, [lawId]);

  const filteredArticles = useMemo(() => {
    if (!indexSearch.trim()) return articles;
    const q = indexSearch.toLowerCase();
    return articles.filter(
      (a) =>
        a.number.toLowerCase().includes(q) ||
        (a.title && a.title.toLowerCase().includes(q)) ||
        a.content.replace(/<[^>]+>/g, ' ').toLowerCase().includes(q)
    );
  }, [articles, indexSearch]);

  if (!lawId) {
    return (
      <main className="lex-page">
        <section className="lex-surface-card lex-card">
          <p style={{ margin: 0, color: 'var(--lex-danger)' }}>Falta el identificador de la ley.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="lex-page">
      <div className="lex-stack">
        <div className="lex-section-kicker" style={{ marginBottom: -2 }}>
          <Link to="/leyes" style={{ textDecoration: 'none', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <i className="ri-arrow-left-line" aria-hidden />
            Volver a DUMAS
          </Link>
        </div>

        {error ? (
          <section className="lex-surface-card lex-card-tight">
            <p style={{ margin: 0, color: 'var(--lex-danger)', fontSize: 14 }}>{presentLexError(error)}</p>
          </section>
        ) : null}

        {loading ? (
          <section className="lex-surface-card lex-empty">
            <div className="lex-empty-icon">
              <i className="ri-loader-4-line" aria-hidden />
            </div>
            <p style={{ margin: 0 }}>Cargando ley…</p>
          </section>
        ) : !law ? (
          <section className="lex-surface-card lex-empty">
            <p style={{ margin: 0 }}>Ley no encontrada.</p>
          </section>
        ) : (
          <>
            <section className="lex-surface-card lex-card">
              <div className="lex-section-kicker" style={{ marginBottom: 8 }}>
                <i className="ri-scales-3-line" aria-hidden />
                Detalle de ley
              </div>
              <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.1, letterSpacing: '-0.05em', fontWeight: 900, color: 'var(--lex-on-surface)' }}>
                {law.title}
              </h1>
              <div className="lex-chip-row" style={{ marginTop: 14 }}>
                {formatLawDate(law.publishedAt) ? (
                  <span className="lex-chip">
                    <i className="ri-calendar-line" aria-hidden />
                    DOF: {formatLawDate(law.publishedAt)}
                  </span>
                ) : null}
                <span className="lex-chip">
                  <i className="ri-file-list-3-line" aria-hidden />
                  {articles.length} artículos
                </span>
              </div>
            </section>

            {law.description ? (
              <section className="lex-surface-card lex-card">
                <div className="lex-section-kicker" style={{ marginBottom: 8 }}>
                  Contexto
                </div>
                <p style={{ margin: 0, color: 'var(--lex-on-surface-variant)', fontSize: 14, lineHeight: 1.6 }}>
                  {cleanDescription(law.description)}
                </p>
              </section>
            ) : null}

            <section className="lex-surface-card lex-card">
              <div className="lex-section-head" style={{ marginBottom: 14 }}>
                <div>
                  <div className="lex-section-kicker">
                    <i className="ri-file-text-line" aria-hidden />
                    Navegación interna
                  </div>
                  <h2 className="lex-section-title">Artículos</h2>
                </div>
                {articles.length > 0 ? (
                  <button type="button" className="lex-button-secondary" onClick={() => setShowArticleIndex(true)}>
                    <i className="ri-list-check" aria-hidden />
                    Índice
                  </button>
                ) : null}
              </div>

              {articles.length === 0 ? (
                <div className="lex-empty" style={{ padding: 10 }}>
                  <p style={{ margin: 0 }}>Sin artículos disponibles.</p>
                </div>
              ) : (
                <div className="lex-stack">
                  {articles.map((art) => {
                    const snippet = art.content.replace(/<[^>]+>/g, ' ').trim().slice(0, 110);
                    return (
                      <Link
                        key={art.id}
                        to={`/leyes/${lawId}/articulo/${art.id}`}
                        className="lex-surface-card"
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 14,
                          padding: 16,
                          textDecoration: 'none',
                        }}
                      >
                        <div
                          style={{
                            width: 46,
                            height: 46,
                            borderRadius: 16,
                            background: 'linear-gradient(145deg, var(--lex-primary) 0%, rgba(16,32,59,0.92) 100%)',
                            color: 'var(--lex-on-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontWeight: 800,
                            fontSize: 14,
                          }}
                        >
                          {art.number}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="lex-section-kicker" style={{ marginBottom: 6 }}>
                            Artículo
                          </div>
                          <h3 style={{ margin: 0, fontSize: 15, lineHeight: 1.35, color: 'var(--lex-on-surface)' }}>
                            {art.title || `Artículo ${art.number}`}
                          </h3>
                          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--lex-on-surface-variant)', lineHeight: 1.5 }}>
                            {snippet}…
                          </p>
                        </div>
                        <i className="ri-arrow-right-s-line" style={{ fontSize: 22, color: 'var(--lex-on-surface-variant)', flexShrink: 0, marginTop: 4 }} aria-hidden />
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>

            {showArticleIndex && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(7,15,28,0.54)',
                  zIndex: 40,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}
                onClick={() => setShowArticleIndex(false)}
              >
                <div
                  className="lex-surface-card"
                  style={{
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    maxHeight: '84vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ padding: 18, borderBottom: '1px solid var(--lex-outline)' }}>
                    <div className="lex-section-head" style={{ marginBottom: 12 }}>
                      <div>
                        <div className="lex-section-kicker">Índice rápido</div>
                        <h2 className="lex-section-title">Artículos de la ley</h2>
                      </div>
                      <button type="button" className="lex-icon-button" onClick={() => setShowArticleIndex(false)} aria-label="Cerrar">
                        <i className="ri-close-line" aria-hidden />
                      </button>
                    </div>
                    <div className="lex-field-shell">
                      <i className="ri-search-line lex-field-icon" aria-hidden />
                      <input
                        type="search"
                        placeholder="Buscar artículo…"
                        value={indexSearch}
                        onChange={(e) => setIndexSearch(e.target.value)}
                        className="lex-input has-icon"
                      />
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--lex-on-surface-variant)' }}>{filteredArticles.length} artículos</p>
                  </div>

                  <div style={{ overflowY: 'auto', flex: 1, padding: 12 }}>
                    {filteredArticles.length === 0 ? (
                      <div className="lex-empty">
                        <p style={{ margin: 0 }}>No hay coincidencias.</p>
                      </div>
                    ) : (
                      <div className="lex-stack">
                        {filteredArticles.map((art, idx) => {
                          const color = INDEX_COLORS[idx % INDEX_COLORS.length];
                          const snippet = art.content.replace(/<[^>]+>/g, ' ').trim();
                          const short = snippet.length > 64 ? `${snippet.slice(0, 64)}…` : snippet;
                          return (
                            <button
                              key={art.id}
                              type="button"
                              onClick={() => {
                                setShowArticleIndex(false);
                                navigate(`/leyes/${lawId}/articulo/${art.id}`);
                              }}
                              className="lex-surface-card"
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: 14,
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                            >
                              <div
                                style={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: 14,
                                  background: color,
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 800,
                                  fontSize: 13,
                                  flexShrink: 0,
                                }}
                              >
                                {art.number}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--lex-on-surface)' }}>
                                  {art.title || `Artículo ${art.number}`}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--lex-on-surface-variant)', marginTop: 2 }}>
                                  {short}
                                </div>
                              </div>
                              <i className="ri-arrow-right-s-line" style={{ fontSize: 20, color: 'var(--lex-on-surface-variant)', flexShrink: 0 }} aria-hidden />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
